'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockNewsletterIssues, NewsletterIssue } from '@/data/mockNewsletter';
import { BookOpen, ArrowRight, Calendar } from 'lucide-react';

const categoryFilters = [
  { label: 'All', value: 'all' },
  { label: 'Community Update', value: 'Community Update' },
  { label: 'Resident Spotlight', value: 'Resident Spotlight' },
  { label: 'Services & Guidelines', value: 'Services & Guidelines' },
  { label: 'Privacy & Trust', value: 'Privacy & Trust' },
];

function IssueCardSkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start justify-between mb-3">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-5 w-3/4 mb-2" />
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-5/6 mb-4" />
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );
}

function IssueCard({ issue }: { issue: NewsletterIssue }) {
  return (
    <Link href={`/newsletter/${issue.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#4CC9FF' }}>
            Issue #{issue.issueNumber}
          </span>
          <div className="flex items-center gap-1.5">
            <Calendar size={11} style={{ color: '#A7A3C4' }} />
            <span className="text-[11px]" style={{ color: '#A7A3C4' }}>{issue.date}</span>
          </div>
        </div>

        <h3 className="font-display font-semibold text-[15px] leading-snug mb-2" style={{ color: '#F2F4FF' }}>
          {issue.title}
        </h3>

        <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: '#A7A3C4' }}>
          {issue.excerpt}
        </p>

        <div className="flex items-center justify-between">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{
              background: 'rgba(122,92,255,0.10)',
              border: '1px solid rgba(122,92,255,0.20)',
              color: '#9B7FFF',
            }}
          >
            {issue.category}
          </span>
          <span className="flex items-center gap-1 text-[12px] font-medium transition-colors duration-150 group-hover:opacity-80" style={{ color: '#4CC9FF' }}>
            Read issue <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function NewsletterPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const latestIssue = mockNewsletterIssues[0];
  const archiveIssues = mockNewsletterIssues.slice(1);

  const filteredArchive = archiveIssues.filter((i) =>
    activeFilter === 'all' || i.category === activeFilter
  );

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Private Archive"
          title="Newsletter"
          description="The Panorama community bulletin — resident spotlights, event previews, marketplace highlights, and community updates."
          stat={{ value: String(mockNewsletterIssues.length), label: 'issues published' }}
        />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          {/* Latest issue highlight */}
          {!isLoading && latestIssue && (
            <div className="mb-10">
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full mb-4"
                style={{ background: 'rgba(76,201,255,0.08)', border: '1px solid rgba(76,201,255,0.15)' }}
              >
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#4CC9FF' }} />
                <span className="text-[11px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                  Latest Issue
                </span>
              </div>

              <Link href={`/newsletter/${latestIssue.slug}`} className="block group focus-visible:outline-none">
                <div
                  className="rounded-xl p-6 sm:p-8 group-focus-visible:border-[rgba(76,201,255,0.35)]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(122,92,255,0.10) 0%, rgba(76,201,255,0.06) 100%)',
                    border: '1px solid rgba(122,92,255,0.20)',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(122,92,255,0.35)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 1px rgba(122,92,255,0.12), 0 8px 32px rgba(122,92,255,0.10)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(122,92,255,0.20)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9B7FFF' }}>
                        Issue #{latestIssue.issueNumber}
                      </span>
                      <h2 className="font-display font-bold text-[22px] sm:text-[26px] leading-tight mt-1" style={{ color: '#F2F4FF' }}>
                        {latestIssue.title}
                      </h2>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Calendar size={12} style={{ color: '#A7A3C4' }} />
                      <span className="text-[12px]" style={{ color: '#A7A3C4' }}>{latestIssue.date}</span>
                    </div>
                  </div>
                  <p className="text-[14px] leading-relaxed mb-5" style={{ color: '#A7A3C4' }}>
                    {latestIssue.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {latestIssue.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="flex items-center gap-1 text-[13px] font-semibold" style={{ color: '#9B7FFF' }}>
                      Read issue <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Archive heading + filters */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>
              Archive
            </h2>
            <FilterRow filters={categoryFilters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <IssueCardSkeleton key={i} />)}
            </div>
          )}

          {/* Empty */}
          {!isLoading && filteredArchive.length === 0 && (
            <EmptyState
              title="No issues found"
              description="Try a different category filter to browse the archive."
              icon={<BookOpen size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveFilter('all')}>
                  Show all issues
                </button>
              }
            />
          )}

          {/* Grid */}
          {!isLoading && filteredArchive.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredArchive.map((issue) => (
                <IssueCard key={issue.id} issue={issue} />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
